import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {ExpressCheckoutElement, useStripe, useElements} from '@stripe/react-stripe-js'

import {Text, Button} from '@salesforce/retail-react-app/app/components/shared/ui'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {
    useAccessToken,
    useShopperCustomersMutation,
    useShopperBasketsMutation,
    useShopperBasketsMutationHelper
} from '@salesforce/commerce-sdk-react'
import {useCurrentBasket} from '../../hooks/use-current-basket'
import {useSiteCustomPreferences} from '../../hooks/use-site-custom-preferences'

import CreatePaymentMethod from '../../custom-api/payment-methods/stripe/create-payment-method'
import {deleteTempBasket} from '../../custom-api/basket/delete-temp-basket'
import {getShippingMethodsForShipment} from '../../custom-api/basket/get-shipping-methods-for-shippment'
import {
    formatAddress,
    getShippingOptions,
    handleShipmentDetails,
    handleShippingRateChange,
    handleShippingAddressChange,
    priceRoudOff
} from '../../utils/stripe-utils'

const StripePDPExpressCheckout = (props) => {
    const {site} = useMultiSite()
    const navigate = useNavigation()
    const appOrigin = useAppOrigin()
    const {getTokenWhenReady} = useAccessToken()
    const {data: customer} = useCurrentCustomer()
    const {variant, product, quantity, priceData, onGooglePayStatusChange} = props
    const productInStock = product?.inventory?.ats > 0
    const {data: basket, refetch} = useCurrentBasket()
    const {addItemToNewOrExistingBasket} = useShopperBasketsMutationHelper()
    const updateCustomerMutation = useShopperCustomersMutation('updateCustomer')
    const {mutateAsync: UpdateShipmentForBasket} =
        useShopperBasketsMutation('updateShipmentForBasket')
    const {mutateAsync: updateShippingMethod} = useShopperBasketsMutation(
        'updateShippingMethodForShipment'
    )
    const {mutateAsync: updateShippingAddressForShipment} = useShopperBasketsMutation(
        'updateShippingAddressForShipment'
    )
    const {mutateAsync: updateBasket} = useShopperBasketsMutation('updateBasket')
    const removeItemFromBasketMutation = useShopperBasketsMutation('removeItemFromBasket')
    const removeGiftCertificateItemFromBasketMutation = useShopperBasketsMutation(
        'removeGiftCertificateItemFromBasket'
    )
    const stripe = useStripe()
    const elements = useElements()
    const configs = getConfig()

    const [authToken, setAuthToken] = useState()
    const [tempBasket, setTempBasket] = useState()
    const [errorMessage, setErrorMessage] = useState()
    const [isButtonLoaded, setIsButtonLoaded] = useState(true) // Initialize as true to show button initially
    const {getcustomPreferencevalue} = useSiteCustomPreferences()
    const payments = getcustomPreferencevalue('pwaPaymentsOnPDP')
    const PaymentMethodsOrder = payments?.filter((method) => method !== 'buyNow')

    const paymentMethods = {}
    PaymentMethodsOrder?.forEach((method) => {
        paymentMethods[method] = 'auto'
    })

    useEffect(() => {
        const getToken = async () => {
            const token = await getTokenWhenReady()
            setAuthToken(token)
        }

        getToken()
    }, [])

    useEffect(() => {
        const fetchBasket = async () => {
            await refetch()
        }
        if (tempBasket && Object.keys(tempBasket).length > 0) fetchBasket()
    }, [tempBasket])

    // Handle when the Express Checkout Element is ready
    const handleElementReady = (event) => {
        const buttonLoaded = !!event.availablePaymentMethods
        setIsButtonLoaded(buttonLoaded)

        if (onGooglePayStatusChange) {
            onGooglePayStatusChange(buttonLoaded)
        }
    }

    // Handle errors from the Express Checkout Element
    const handleElementError = () => {
        setIsButtonLoaded(false)

        if (onGooglePayStatusChange) {
            onGooglePayStatusChange(false)
        }
    }

    const deleteExistingLineItemsInBasket = async (basketId, pliIds, gcliIds) => {
        if (!basketId) return
        if (pliIds && pliIds.length > 0)
            await Object.values(pliIds).reduce(
                (promise, pliId) =>
                    promise.then(() =>
                        removeItemFromBasketMutation.mutateAsync({
                            parameters: {
                                basketId: basketId,
                                itemId: pliId
                            }
                        })
                    ),
                Promise.resolve()
            )
        if (gcliIds && gcliIds.length > 0)
            await Object.values(gcliIds).reduce(
                (promise, gcliId) =>
                    promise.then(() =>
                        removeGiftCertificateItemFromBasketMutation.mutateAsync({
                            parameters: {
                                basketId: basket.basketId,
                                giftCertificateItemId: gcliId
                            }
                        })
                    ),
                Promise.resolve()
            )
    }

    // executes when user clicks on express cta
    const onClick = async ({resolve, reject}) => {
        try {
            const allowedShippingCountries = configs?.app?.library?.googlePayCountries?.split(
                ','
            ) || ['US', 'CA']
            const options = {
                shippingAddressRequired: true,
                allowedShippingCountries: allowedShippingCountries,
                emailRequired: true,
                phoneNumberRequired: true,
                shippingRates: [
                    {
                        id: '001',
                        displayName: 'Standard Shipping',
                        amount: priceRoudOff(8.99)
                    }
                ]
            }

            resolve(options)

            try {
                // delete existing products line items from basket
                if (basket?.productItems?.length > 0 || basket?.giftCertificateItems?.length > 0) {
                    const pliIds = basket?.productItems?.map(({itemId}) => itemId) ?? []
                    const gcliIds = basket?.giftCertificateItems?.map(
                        ({giftCertificateItemId}) => giftCertificateItemId
                    )
                    await deleteExistingLineItemsInBasket(basket?.basketId, pliIds, gcliIds)
                }

                // add items to basket
                const productId = product?.type?.bundle ? product.id : variant?.productId
                const productItems = [
                    {
                        productId,
                        price: variant.price,
                        quantity
                    }
                ]
                const currentBasket = await addItemToNewOrExistingBasket(productItems)
                setTempBasket(currentBasket)
            } catch (basketError) {
                logger.error('Basket creation failed', basketError)
            }
        } catch (error) {
            logger.error('stripe pdp express checkout', {
                namespace: 'stripe.pdp.expresschekcout.onClick',
                additionalProperties: {error: error.message}
            })
            reject()
        }
    }

    // executes on loading and when user switches shipping address
    const onShippingAddressChange = async ({resolve, reject, address}) => {
        try {
            const shipmentId = basket?.shipments[0]?.shipmentId
            const shippingAddress = await formatAddress(address)
            await handleShippingAddressChange(
                basket,
                shippingAddress,
                updateShippingAddressForShipment
            )
            const shippingOptions = await getShippingMethodsForShipment(
                appOrigin,
                authToken,
                basket?.basketId,
                shipmentId
            )
            const shippingMethodId =
                shippingOptions?.applicableShippingMethods?.[0]?.id ||
                shippingOptions?.defaultShippingMethodId
            // update shipping address
            const updateAddressResult = await handleShipmentDetails(
                basket,
                shippingAddress,
                shipmentId,
                shippingMethodId,
                UpdateShipmentForBasket
            )
            setTempBasket(updateAddressResult)
            const orderTotal = updateAddressResult?.orderTotal
            elements.update({amount: priceRoudOff(orderTotal)})
            resolve({
                shippingRates: await getShippingOptions(shippingOptions)
            })
        } catch (error) {
            logger.error('stripe PDP express checkout', {
                namespace: 'stripe.pdp.expresschekcout.onShippingAddressChange',
                additionalProperties: {error: error.message}
            })
            reject()
        }
    }

    // executed when we change shipping methods
    const onShippingRateChange = async ({resolve, reject, shippingRate}) => {
        try {
            const updateShippingRateResult = await handleShippingRateChange(
                updateShippingMethod,
                authToken,
                site,
                shippingRate,
                tempBasket
            )
            setTempBasket(updateShippingRateResult)
            const orderTotal = updateShippingRateResult?.orderTotal
            elements.update({amount: priceRoudOff(orderTotal)})
            resolve()
        } catch (error) {
            logger.error('stripe PDP express checkout', {
                namespace: 'stripe.pdp.expresschekcout.onShippingRateChange',
                additionalProperties: {error: error.message}
            })
            reject()
        }
    }

    // it will exeute when user click on pay cta on express checkout modal
    const onConfirm = async (event) => {
        console.log(event)
        if (!stripe) {
            return
        }
        if (typeof window !== 'undefined')
            localStorage.setItem('stripe_payment_method', 'CREDIT_CARD')

        const {error: submitError} = await elements.submit()
        if (submitError) {
            setErrorMessage(submitError.message)
            return
        }

        try {
            // create payment method and attach it with stripe customer ID
            const paymentMethodResult = await CreatePaymentMethod(
                basket,
                customer,
                updateCustomerMutation,
                getcustomPreferencevalue,
                stripe,
                elements,
                event
            )
            if (paymentMethodResult?.error) {
                setErrorMessage(paymentMethodResult?.error)
                return
            }

            event.pmId = paymentMethodResult?.paymentMethod?.id
            await updateBasket({
                parameters: {basketId: basket?.basketId},
                body: {
                    c_stripePaymentMethodID: event?.expressPaymentType,
                    c_isExpressCheckout: true,
                    c_pwaOrder: true,
                    c_stripeEventData: JSON.stringify(event)
                }
            })
            // redirect to SFRA
            navigate('/process')
        } catch (error) {
            logger.error('stripe PDP express checkout', {
                namespace: 'stripe.pdp.expresschekcout.onConfirm',
                additionalProperties: {error: error.message}
            })
            setErrorMessage(
                'For technical reasons, your request could not be handled properly at this time. We apologize for any inconvenience.'
            )
        }
    }

    const onCancel = async () => {
        try {
            // delete temp basket
            await deleteTempBasket(getTokenWhenReady, tempBasket)
            // update element
            elements.update({amount: priceRoudOff(priceData?.currentPrice)})
            window.location.reload()
        } catch (error) {
            logger.error('stripe PDP express checkout', {
                namespace: 'stripe.pdp.expresschekcout.onCancel',
                additionalProperties: {error: error.message}
            })
        }
    }

    // handling payment method to show or not (auto means yes and never means no)
    // also we can handle which payment method orders to show on page
    const elementOptions = {
        paymentMethods: paymentMethods,
        paymentMethodOrder: PaymentMethodsOrder,
        buttonHeight: 48
    }

    return (
        <Button
            key="payment-button"
            width={'100%'}
            display={isButtonLoaded ? {base: 'block', sm: 'inline-block'} : 'none'}
            sx={{
                _hover: {
                    cursor: !productInStock ? 'not-allowed' : 'pointer'
                }
            }}
            disabled={!productInStock}
            isDisabled={!productInStock}
            variant={''}
            p={'0'}
            borderRadius={'33px'}
            height={'auto !important'}
            data-testid={'express-cta-button'}
        >
            <ExpressCheckoutElement
                options={elementOptions}
                onClick={onClick}
                onReady={handleElementReady}
                onShippingAddressChange={onShippingAddressChange}
                onShippingRateChange={onShippingRateChange}
                onConfirm={onConfirm}
                onCancel={onCancel}
                onError={handleElementError}
            />

            {errorMessage && <Text color={'red'}>{errorMessage}</Text>}
        </Button>
    )
}

StripePDPExpressCheckout.propTypes = {
    variant: PropTypes.object,
    product: PropTypes.object,
    quantity: PropTypes.number,
    priceData: PropTypes.any,
    onGooglePayStatusChange: PropTypes.func
}
export default StripePDPExpressCheckout
